import { MENU_ITEM_TYPE } from './enums';

/**
 * Reconciles selections saved on a past order against the item's *current*
 * shape in the menu.
 *
 * A saved order stores the selections the customer made at the time, but the
 * menu item can be restructured afterwards. The case this exists for: an item
 * that was simple (itemType 0, priced through `selectedSizes`) when it was
 * ordered and has since been converted to complex (itemType 1, priced through
 * `selectedModifiers`). Replaying the old selections into the new shape leaves
 * a complex item with an empty modifier tree, which prices at 0.00 in both
 * getItemPrice and getItemCustomizedPrice.
 *
 * Anything that cannot be rebuilt with certainty returns null, so the caller
 * can route the item back through the item view instead of guessing on the
 * customer's behalf.
 */

const normalizeName = (name) => String(name ?? '').trim().toLowerCase();

// Menu data marks opt-outs by name ("no cheese"), the same convention
// getGroupedModifiers relies on. They are defaults, not customer choices.
const isNoOption = (name) => normalizeName(name).split(' ').includes('no');

/**
 * True when the saved item cannot price itself against the current menu item
 * because the item's pricing structure changed underneath it.
 */
export const hasStructureDrifted = (savedItem, liveMenuItem) =>
  Number(liveMenuItem?.itemType) === MENU_ITEM_TYPE.complex &&
  !(savedItem?.selectedModifiers?.length > 0);

/**
 * Rebuilds a base sub modifier from the size the customer actually ordered.
 * The base modifier's sub modifiers are the sizes - addToOrder keeps the two
 * as the same objects (`itemObj.selectedSizes = m.subModifiers`), so both
 * pricing paths have to see the same shape here.
 */
const buildBaseSubModifier = (subModifier, savedSize, isSingleSize) => {
  const quantity = Number(savedSize.quantity) || 1;
  const selectedVariantIds = new Set(
    (savedSize.variants ?? [])
      .filter((v) => Number(v.quantity) > 0)
      .map((v) => v._id)
  );

  return {
    ...subModifier,
    quantity,
    isSelected: true,
    isSingleSize,
    cName: savedSize.cName ?? '',
    variants: Array.isArray(subModifier.variants)
      ? subModifier.variants
          .filter((v) => selectedVariantIds.has(v._id))
          .map((v) => ({ ...v, quantity, isSelected: true }))
      : null,
  };
};

/**
 * A non base modifier prices itself per base sub modifier through `prices`.
 * The saved order holds no answer for these groups - the customer was never
 * asked, the item had no modifiers back then - so a group is only safe to
 * settle without them when either there is nothing to choose, or the choice
 * cannot move the price.
 */
const resolveNonBaseModifier = (modifier, baseSubModifiers) => {
  const subModifiers = modifier?.subModifiers ?? [];
  if (subModifiers.length === 0) return { ...modifier, subModifiers: [] };

  const baseQuantities = new Map(
    baseSubModifiers.map((s) => [s._id, Number(s.quantity) || 1])
  );

  const pricesForOrderedSizes = (subModifier) =>
    (subModifier.prices ?? []).filter((p) => baseQuantities.has(p._id));

  const options = subModifiers.filter((s) => !isNoOption(s.name));

  // Nothing to choose: one real option means every new order of this item
  // carries it, so it is selected at today's price.
  if (options.length === 1) {
    const prices = pricesForOrderedSizes(options[0]).map((p) => ({
      ...p,
      isSelected: true,
      quantity: baseQuantities.get(p._id),
      variants: Array.isArray(p.variants) ? [] : (p.variants ?? null),
    }));
    if (prices.length === 0) return null;
    return {
      ...modifier,
      subModifiers: [{ ...options[0], isSelected: true, prices }],
    };
  }

  // A real choice, but one that cannot change what the customer pays. Leaving
  // it unselected keeps the price honest; the item view is where a customer
  // picks it on a fresh order.
  const canChangePrice = subModifiers.some((s) =>
    pricesForOrderedSizes(s).some((p) => Number(p.price) > 0)
  );
  if (!canChangePrice) return { ...modifier, subModifiers: [] };

  // A priced choice we have no answer for. Guessing here would either
  // undercharge or put an incomplete item in front of the kitchen.
  return null;
};

/**
 * @param savedItem menu item as stored on the past order
 * @param liveMenuItem same item as it exists in the menu today, already run
 *   through getMenuItemForInstore so its prices are current
 * @returns { selectedSizes, selectedModifiers } or null when the item needs
 *   the customer to make a choice they were never asked for
 */
export const reconcileDriftedItem = (savedItem, liveMenuItem) => {
  const liveModifiers = liveMenuItem?.selectedModifiers ?? [];
  const baseModifier = liveModifiers.find(
    (m) => m._id === liveMenuItem?.baseModifierId
  );
  if (!baseModifier?.subModifiers?.length) return null;

  const savedSizes = (savedItem?.selectedSizes ?? []).filter(
    (s) => Number(s.quantity) > 0
  );
  if (savedSizes.length === 0) return null;

  const isSingleSize = baseModifier.subModifiers.length === 1;
  const baseSubModifiers = [];

  for (const savedSize of savedSizes) {
    // Ids survive a conversion that reuses the size rows; fall back to the
    // name for a conversion that re-issued them ("serve" -> "serve").
    const subModifier =
      baseModifier.subModifiers.find((s) => s._id === savedSize._id) ??
      baseModifier.subModifiers.find(
        (s) => normalizeName(s.name) === normalizeName(savedSize.name)
      );
    // The size the customer ordered is gone from the menu entirely.
    if (!subModifier) return null;
    baseSubModifiers.push(
      buildBaseSubModifier(subModifier, savedSize, isSingleSize)
    );
  }

  const otherModifiers = [];
  for (const modifier of liveModifiers) {
    if (modifier._id === baseModifier._id) continue;
    const resolved = resolveNonBaseModifier(modifier, baseSubModifiers);
    if (!resolved) return null;
    otherModifiers.push(resolved);
  }

  return {
    selectedSizes: baseSubModifiers,
    selectedModifiers: [
      { ...baseModifier, subModifiers: baseSubModifiers },
      ...otherModifiers,
    ],
  };
};
